import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Container,
  Card,
  CardContent,
  Grid,
  Paper,
  Tabs,
  Tab
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Timeline as TimelineIcon,
  Star as StarIcon,
  Article as ArticleIcon,
  PhotoCamera as PhotoCameraIcon,
  Code as CodeIcon,
  QuestionAnswer as QAIcon
} from '@mui/icons-material';
import LazyDataViewer from '../components/LazyDataViewer';
import api from '../services/api';

const SnapshotViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);

  const fetchSnapshot = useCallback(async () => {
    try {
      const response = await api.get(`/api/snapshots/${id}`);
      setSnapshot(response.data);
    } catch (error) {
      console.error('Error fetching snapshot:', error);
      setError('Failed to load snapshot');
    }
  }, [id]);

  useEffect(() => {
    fetchSnapshot();
  }, [fetchSnapshot]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  if (!snapshot) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2, color: 'text.secondary' }}
          >
            <ArrowBackIcon />
          </Button>
          <Box>
            <Typography 
              variant="h3" 
              component="h1" 
              sx={{ 
                fontWeight: 'bold',
                color: '#ffffff',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                mb: 2
              }}
              gutterBottom
            >
              Snapshot Details
            </Typography>
            <Typography variant="h6" color="textSecondary">
              {formatDate(snapshot.captureDate)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Snapshot Information */}
      <Card className="modern-card" sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimelineIcon color="primary" />
            Snapshot Information
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">
                <strong>Capture Date:</strong> {formatDate(snapshot.captureDate)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Profile ID:</strong> {snapshot.profileId}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary">
                <strong>Duration:</strong> {snapshot.metadata?.captureDurationMs || 0}ms
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Data Types:</strong> {snapshot.metadata?.dataTypes?.join(', ') || 'None'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Tabs */}
      <Card className="modern-card">
        <CardContent>
          <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
            <Tab label="Reviews" icon={<StarIcon />} iconPosition="start" />
            <Tab label="Posts" icon={<ArticleIcon />} iconPosition="start" />
            <Tab label="Q&A" icon={<QAIcon />} iconPosition="start" />
            <Tab label="Photos" icon={<PhotoCameraIcon />} iconPosition="start" />
            <Tab label="Raw Data" icon={<CodeIcon />} iconPosition="start" />
          </Tabs>

          <Box sx={{ mt: 2 }}>
            {/* Reviews Tab */}
            {selectedTab === 0 && (
              <Box>
                {snapshot.rawData?.reviews ? (
                  <LazyDataViewer
                    snapshotId={snapshot._id}
                    dataType="reviews"
                    title="⭐ Reviews"
                    icon={StarIcon}
                  />
                ) : (
                  <Alert severity="error">
                    Reviews data not available. Status: {snapshot.rawData?.reviews?.status || 'Unknown'}
                  </Alert>
                )}
              </Box>
            )}

            {/* Posts Tab */}
            {selectedTab === 1 && (
              <Box>
                {snapshot.rawData?.posts ? (
                  <LazyDataViewer
                    snapshotId={snapshot._id}
                    dataType="posts"
                    title="📝 Posts"
                    icon={ArticleIcon}
                  />
                ) : (
                  <Alert severity="error">
                    Posts data not available. Status: {snapshot.rawData?.posts?.status || 'Unknown'}
                  </Alert>
                )}
              </Box>
            )}

            {/* Q&A Tab */}
            {selectedTab === 2 && (
              <Box>
                {snapshot.rawData?.qa ? (
                  <LazyDataViewer
                    snapshotId={snapshot._id}
                    dataType="qa"
                    title="❓ Q&A"
                    icon={QAIcon}
                  />
                ) : (
                  <Alert severity="error">
                    Q&A data not available. Status: {snapshot.rawData?.qa?.status || 'Unknown'}
                  </Alert>
                )}
              </Box>
            )}

            {/* Photos Tab */}
            {selectedTab === 3 && (
              <Box>
                {snapshot.rawData?.photos ? (
                  <LazyDataViewer
                    snapshotId={snapshot._id}
                    dataType="photos"
                    title="📸 Photos"
                    icon={PhotoCameraIcon}
                  />
                ) : (
                  <Alert severity="error">
                    Photos data not available. Status: {snapshot.rawData?.photos?.status || 'Unknown'}
                  </Alert>
                )}
              </Box>
            )}

            {/* Raw JSON Tab */}
            {selectedTab === 4 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  🔧 Raw JSON Data
                </Typography>
                <Paper sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                  <pre style={{ 
                    fontSize: '12px', 
                    lineHeight: '1.4',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {JSON.stringify(snapshot.rawData, null, 2)}
                  </pre>
                </Paper>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default SnapshotViewer; 